import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import {
  AlertTriangle, MessageCircle, LifeBuoy, Clock, ShieldCheck,
  ChevronDown, ChevronUp, Loader2, ArrowUpRight,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// ──────────────────────────────────────────────────────────────────────────────
// Module config — single source of truth for the 5 ops modules on this screen.
// API paths and statuses match the v4 ops contract exactly as specified.
// ──────────────────────────────────────────────────────────────────────────────
const MODULES = [
  {
    key: "ndr",
    label: "NDR",
    icon: AlertTriangle,
    color: "amber",
    api: "/v2/ndr",
    params: { status: "PENDING,RE_ATTEMPT_SCHEDULED" },
    viewAll: "/admin/ndr",
    columns: ["AWB", "Customer", "Attempts", "Status", "Last Updated"],
  },
  {
    key: "disputes",
    label: "Disputes",
    icon: MessageCircle,
    color: "red",
    api: "/v2/disputes",
    params: { status: "OPEN,ESCALATED" },
    viewAll: "/admin/disputes",
    columns: ["ID", "AWB", "Type", "Status", "Age (days)"],
  },
  {
    key: "support",
    label: "Support",
    icon: LifeBuoy,
    color: "orange",
    api: "/v2/support",
    params: { priority: "HIGH,CRITICAL", status: "OPEN" },
    viewAll: "/admin/support",
    columns: ["Ticket ID", "Subject", "Priority", "Status", "SLA"],
  },
  {
    key: "demurrage",
    label: "Demurrage",
    icon: Clock,
    color: "purple",
    api: "/v2/demurrage",
    params: { status: "CALCULATED" },
    viewAll: "/admin/demurrage",
    columns: ["AWB", "Customer", "Days", "Amount", "Status"],
  },
  {
    key: "insurance",
    label: "Insurance",
    icon: ShieldCheck,
    color: "blue",
    api: "/v2/insurance-claims",
    params: { status: "SUBMITTED,UNDER_REVIEW" },
    viewAll: "/admin/insurance-claims",
    columns: ["Claim ID", "AWB", "Declared Value", "Status", "Submitted"],
  },
];

const COLOR_STYLES = {
  amber:  { ring: "ring-amber-200",  text: "text-amber-700",  bg: "bg-amber-50",  dot: "bg-amber-500"  },
  red:    { ring: "ring-red-200",    text: "text-red-700",    bg: "bg-red-50",    dot: "bg-red-500"    },
  orange: { ring: "ring-orange-200", text: "text-orange-700", bg: "bg-orange-50", dot: "bg-orange-500" },
  purple: { ring: "ring-purple-200", text: "text-purple-700", bg: "bg-purple-50", dot: "bg-purple-500" },
  blue:   { ring: "ring-blue-200",   text: "text-blue-700",   bg: "bg-blue-50",   dot: "bg-blue-500"   },
};

// ──────────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────────
function extractList(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  return data.items || data.results || data.data || [];
}

function extractTotal(data, list) {
  if (!data) return list.length;
  return data.total ?? data.count ?? data.total_count ?? list.length;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function formatDateShort(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function daysSince(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24)));
}

function formatINR(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

function isSlaBreached(row) {
  if (typeof row.sla_breached === "boolean") return row.sla_breached;
  const due = row.sla_due_at || row.sla_deadline || row.sla_due;
  if (!due) return false;
  const dueD = new Date(due);
  if (Number.isNaN(dueD.getTime())) return false;
  return dueD.getTime() < Date.now();
}

function slaLabel(row) {
  if (isSlaBreached(row)) return "Breached";
  const due = row.sla_due_at || row.sla_deadline || row.sla_due;
  if (!due) return row.sla_status || "—";
  const remainingH = Math.round((new Date(due).getTime() - Date.now()) / (1000 * 60 * 60));
  if (remainingH <= 0) return "Breached";
  if (remainingH < 24) return `${remainingH}h left`;
  return `${Math.floor(remainingH / 24)}d left`;
}

// ──────────────────────────────────────────────────────────────────────────────
// Row renderer per module
// ──────────────────────────────────────────────────────────────────────────────
function ModuleRow({ moduleKey, row }) {
  if (moduleKey === "ndr") {
    return (
      <TableRow data-testid={`ops-ndr-row-${row.id || row.ndr_id || row.awb}`}>
        <TableCell className="font-medium font-mono text-xs">{row.awb || row.lr_number || "—"}</TableCell>
        <TableCell className="text-sm">{row.customer_name || row.customer || row.customer_id || "—"}</TableCell>
        <TableCell className="text-sm">{row.attempts ?? row.attempt_count ?? "—"}</TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{row.status || "—"}</Badge></TableCell>
        <TableCell className="text-xs text-slate-500">{formatDate(row.updated_at || row.last_updated_at || row.created_at)}</TableCell>
      </TableRow>
    );
  }
  if (moduleKey === "disputes") {
    return (
      <TableRow data-testid={`ops-disputes-row-${row.id || row.dispute_id || row.awb}`}>
        <TableCell className="font-mono text-xs">{row.dispute_id || row.id || "—"}</TableCell>
        <TableCell className="font-mono text-xs">{row.awb || row.lr_number || "—"}</TableCell>
        <TableCell className="text-sm">{row.type || row.dispute_type || "—"}</TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{row.status || "—"}</Badge></TableCell>
        <TableCell className="text-sm">{daysSince(row.created_at || row.raised_at)}</TableCell>
      </TableRow>
    );
  }
  if (moduleKey === "support") {
    const breached = isSlaBreached(row);
    return (
      <TableRow data-testid={`ops-support-row-${row.id || row.ticket_id}`}>
        <TableCell className="font-mono text-xs">{row.ticket_id || row.id || "—"}</TableCell>
        <TableCell className="text-sm max-w-[280px] truncate">{row.subject || row.title || "—"}</TableCell>
        <TableCell>
          <Badge variant="outline" className={
            row.priority === "CRITICAL" ? "border-red-300 text-red-700 bg-red-50 text-xs" :
            row.priority === "HIGH" ? "border-orange-300 text-orange-700 bg-orange-50 text-xs" :
            "text-xs"
          }>
            {row.priority || "—"}
          </Badge>
        </TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{row.status || "—"}</Badge></TableCell>
        <TableCell>
          <Badge
            variant="outline"
            className={breached
              ? "border-red-400 bg-red-100 text-red-700 text-xs font-semibold"
              : "text-xs"
            }
            data-testid={breached ? "ops-support-sla-breached" : "ops-support-sla-ok"}
          >
            {slaLabel(row)}
          </Badge>
        </TableCell>
      </TableRow>
    );
  }
  if (moduleKey === "demurrage") {
    return (
      <TableRow data-testid={`ops-demurrage-row-${row.id || row.awb}`}>
        <TableCell className="font-mono text-xs">{row.awb || row.lr_number || "—"}</TableCell>
        <TableCell className="text-sm">{row.customer_name || row.customer || row.customer_id || "—"}</TableCell>
        <TableCell className="text-sm">{row.days ?? row.demurrage_days ?? "—"}</TableCell>
        <TableCell className="text-sm font-semibold">{formatINR(row.amount ?? row.demurrage_amount ?? row.charge_amount)}</TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{row.status || "—"}</Badge></TableCell>
      </TableRow>
    );
  }
  if (moduleKey === "insurance") {
    return (
      <TableRow data-testid={`ops-insurance-row-${row.id || row.claim_id}`}>
        <TableCell className="font-mono text-xs">{row.claim_id || row.id || "—"}</TableCell>
        <TableCell className="font-mono text-xs">{row.awb || row.lr_number || "—"}</TableCell>
        <TableCell className="text-sm font-semibold">{formatINR(row.declared_value ?? row.consignment_value)}</TableCell>
        <TableCell><Badge variant="outline" className="text-xs">{row.status || "—"}</Badge></TableCell>
        <TableCell className="text-xs text-slate-500">{formatDateShort(row.submitted_at || row.created_at)}</TableCell>
      </TableRow>
    );
  }
  return null;
}

// ──────────────────────────────────────────────────────────────────────────────
// Stat Card
// ──────────────────────────────────────────────────────────────────────────────
function StatCard({ module, count, loading }) {
  const styles = COLOR_STYLES[module.color];
  const Icon = module.icon;
  return (
    <Card
      data-testid={`ops-stat-card-${module.key}`}
      className={`relative overflow-hidden border border-slate-200 hover:shadow-md transition-shadow`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${styles.dot}`} />
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{module.label}</p>
            </div>
            <div className="mt-3">
              {loading ? (
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" data-testid={`ops-stat-loading-${module.key}`} />
              ) : (
                <p className={`text-3xl font-bold ${styles.text}`} data-testid={`ops-stat-count-${module.key}`}>
                  {count}
                </p>
              )}
              <p className="text-xs text-slate-400 mt-0.5">urgent items</p>
            </div>
          </div>
          <div className={`p-2 rounded-lg ${styles.bg}`}>
            <Icon className={`w-5 h-5 ${styles.text}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Collapsible Module Panel
// ──────────────────────────────────────────────────────────────────────────────
function ModulePanel({ module, rows, count, loading, error }) {
  const [open, setOpen] = useState(true);
  const styles = COLOR_STYLES[module.color];
  const Icon = module.icon;

  return (
    <Card className="border border-slate-200" data-testid={`ops-panel-${module.key}`}>
      <div
        className="flex items-center justify-between px-5 py-4 border-b border-slate-100 cursor-pointer hover:bg-slate-50/60 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`ops-panel-toggle-${module.key}`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-1.5 rounded-md ${styles.bg}`}>
            <Icon className={`w-4 h-4 ${styles.text}`} />
          </div>
          <h3 className="font-semibold text-slate-800 text-sm tracking-tight">{module.label}</h3>
          {!loading && (
            <Badge variant="outline" className={`text-xs ${styles.text} border-current`}>
              {count}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Link
            to={module.viewAll}
            onClick={(e) => e.stopPropagation()}
            className="text-xs font-semibold text-slate-600 hover:text-brand-600 flex items-center gap-1"
            data-testid={`ops-panel-viewall-${module.key}`}
          >
            View All <ArrowUpRight className="w-3 h-3" />
          </Link>
          {open ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>

      {open && (
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-10" data-testid={`ops-panel-loading-${module.key}`}>
              <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
            </div>
          ) : error ? (
            <div className="text-center py-10 text-sm text-slate-400" data-testid={`ops-panel-error-${module.key}`}>
              Unable to load {module.label.toLowerCase()} items.
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400" data-testid={`ops-panel-empty-${module.key}`}>
              No urgent items
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {module.columns.map((c) => (
                      <TableHead key={c} className="text-xs uppercase tracking-wider text-slate-500">{c}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 10).map((row, idx) => (
                    <ModuleRow key={row.id || row.ndr_id || row.dispute_id || row.ticket_id || row.claim_id || idx} moduleKey={module.key} row={row} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Page
// ──────────────────────────────────────────────────────────────────────────────
export default function OpsOverview() {
  const initial = MODULES.reduce((acc, m) => {
    acc[m.key] = { rows: [], count: 0, loading: true, error: false };
    return acc;
  }, {});
  const [state, setState] = useState(initial);

  const loadOne = useCallback(async (m) => {
    setState((s) => ({ ...s, [m.key]: { ...s[m.key], loading: true, error: false } }));
    try {
      const res = await api.get(m.api, { params: { ...m.params, limit: 10 } });
      const list = extractList(res.data);
      const total = extractTotal(res.data, list);
      setState((s) => ({
        ...s,
        [m.key]: { rows: list, count: total, loading: false, error: false },
      }));
    } catch (_err) {
      setState((s) => ({
        ...s,
        [m.key]: { rows: [], count: 0, loading: false, error: true },
      }));
    }
  }, []);

  useEffect(() => {
    // Fire every module independently — each loads/renders on its own.
    MODULES.forEach((m) => { loadOne(m); });
  }, [loadOne]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="ops-overview-page">
        {/* Header */}
        <div>
          <h1 className="font-heading font-bold text-2xl text-slate-900 tracking-tight" data-testid="ops-overview-title">
            Ops Overview
          </h1>
          <p className="text-sm text-slate-500 mt-1">Items requiring attention right now</p>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ops-stat-row">
          {MODULES.map((m) => (
            <StatCard
              key={m.key}
              module={m}
              count={state[m.key].count}
              loading={state[m.key].loading}
            />
          ))}
        </div>

        {/* Collapsible panels */}
        <div className="space-y-4">
          {MODULES.map((m) => (
            <ModulePanel
              key={m.key}
              module={m}
              rows={state[m.key].rows}
              count={state[m.key].count}
              loading={state[m.key].loading}
              error={state[m.key].error}
            />
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
}
